import { type RefObject, useEffect, useRef, useState } from 'react'

/**
 * Copies `text` and says so for a moment. Where the clipboard is refused (an
 * old browser, an insecure origin) it selects the text in `selectRef`, line
 * breaks and all, for the reader to copy by hand; without one it shows the
 * text in a prompt.
 */
export function CopyButton({
	text,
	selectRef,
	label = 'Kopier tekst',
	className = 'btn btn-sm btn-primary',
}: {
	text: string
	selectRef?: RefObject<HTMLElement | null>
	label?: string
	className?: string
}) {
	const [state, setState] = useState<'idle' | 'copied' | 'selected'>('idle')
	const timer = useRef<ReturnType<typeof setTimeout>>(undefined)
	useEffect(() => () => clearTimeout(timer.current), [])

	const copy = async () => {
		try {
			await navigator.clipboard.writeText(text)
			setState('copied')
		} catch {
			const selection = window.getSelection()
			if (selectRef?.current && selection) {
				selection.selectAllChildren(selectRef.current)
				setState('selected')
			} else {
				window.prompt('Kopier:', text)
			}
		}
		clearTimeout(timer.current)
		timer.current = setTimeout(() => setState('idle'), 2500)
	}

	return (
		<button type="button" className={className} onClick={copy}>
			{state === 'copied'
				? 'Kopiert'
				: state === 'selected'
					? 'Markert – kopier sjølv'
					: label}
		</button>
	)
}
